import {
  Activity,
  Ban,
  KeyRound,
  Link2,
  MoreHorizontal,
  Palmtree,
  Pencil,
  ShieldCheck,
  ShieldOff,
  UserPlus,
  Users,
  UserX,
  Search,
  RotateCcw,
  Table2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  useCancelInvitation,
  useCreateResetLink,
  useResendInvitation,
  useResetUserMfa,
  useTeams,
  useUpdateUser,
  useUsers,
  type LinkResult,
  type Role,
  type Team,
  type User,
} from "@/api/queries";
import { useMe } from "@/app/auth";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { errorText } from "@/lib/errors";
import { formatShortDateTime } from "@/lib/format";

import { VacationDialog } from "@/features/vacations/VacationDialog";

import { EditUserDialog, InviteDialog, LinkDialog } from "./PeopleDialogs";

const roles: Role[] = ["admin", "manager", "employee"];
const statuses: User["status"][] = ["active", "invited", "blocked"];
const statusBadge = { active: "success", invited: "warning", blocked: "danger" } as const;

type Action =
  | { kind: "invite" }
  | { kind: "edit" | "vacation"; user: User }
  | { kind: "link"; mode: "invite" | "reset"; result: LinkResult }
  | { kind: "block" | "unblock" | "cancel" | "resetMfa"; user: User };

export function PeoplePage() {
  const { t } = useTranslation();
  const me = useMe();
  const admin = me.permissions.manage_users;
  const users = useUsers();
  const teams = useTeams();
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<Role | "">("");
  const [status, setStatus] = useState<User["status"] | "">("");
  const [action, setAction] = useState<Action | null>(null);
  const close = () => setAction(null);

  const teamsByUser = useMemo(() => {
    const map = new Map<string, Team[]>();
    for (const team of teams.data ?? []) {
      for (const m of team.members) map.set(m.user_id, [...(map.get(m.user_id) ?? []), team]);
    }
    return map;
  }, [teams.data]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users.data ?? []).filter(
      (u) =>
        (!q || u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)) &&
        (!role || u.role === role) &&
        (!status || u.status === status),
    );
  }, [users.data, query, role, status]);

  const updateUser = useUpdateUser();
  const cancelInvite = useCancelInvitation();
  const resetMfa = useResetUserMfa();

  return (
    <>
      <PageHeader
        title={t("people.title")}
        description={admin ? t("people.subtitle") : t("people.subtitleManager")}
        actions={
          admin && (
            <Button onClick={() => setAction({ kind: "invite" })}>
              <UserPlus /> {t("people.invite")}
            </Button>
          )
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b p-4">
          <div className="relative min-w-56 flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label={t("common.search")}
              placeholder={t("people.search")}
              className="h-9 pl-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <NativeSelect
            aria-label={t("people.columns.role")}
            value={role}
            onChange={(e) => setRole(e.target.value as Role | "")}
          >
            <option value="">{t("people.allRoles")}</option>
            {roles.map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label={t("people.columns.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value as User["status"] | "")}
          >
            <option value="">{t("people.allStatuses")}</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {t(`userStatus.${s}`)}
              </option>
            ))}
          </NativeSelect>
          {users.data && (
            <span className="ml-auto text-sm text-muted">{t("people.count", { count: list.length })}</span>
          )}
        </div>

        {users.isError ? (
          <EmptyState icon={Users} title={t("common.error")} />
        ) : !users.isPending && list.length === 0 ? (
          <EmptyState icon={Users} title={t("people.empty")} />
        ) : (
          <Table className="min-w-[880px]">
            <THead>
              <tr>
                <TH>{t("people.columns.person")}</TH>
                <TH>{t("people.columns.role")}</TH>
                <TH>{t("people.columns.teams")}</TH>
                <TH>{t("people.columns.status")}</TH>
                <TH>{t("people.columns.mfa")}</TH>
                <TH>{t("people.columns.lastSeen")}</TH>
                <TH className="w-12">
                  <span className="sr-only">{t("common.actions")}</span>
                </TH>
              </tr>
            </THead>
            <tbody>
              {users.isPending
                ? [0, 1, 2].map((i) => (
                    <TR key={i}>
                      <TD colSpan={7}>
                        <Skeleton className="h-8" />
                      </TD>
                    </TR>
                  ))
                : list.map((u) => (
                    <PersonRow
                      key={u.id}
                      user={u}
                      teams={teamsByUser.get(u.id) ?? []}
                      isMe={u.id === me.user.id}
                      admin={admin}
                      canMarkVacation={
                        admin ||
                        (teamsByUser.get(u.id) ?? []).some((team) =>
                          team.members.some((m) => m.user_id === me.user.id && m.is_manager),
                        )
                      }
                      onAction={setAction}
                    />
                  ))}
            </tbody>
          </Table>
        )}
      </Card>

      {action?.kind === "invite" && (
        <InviteDialog
          teams={teams.data ?? []}
          onClose={close}
          onInvited={(result) => setAction({ kind: "link", mode: "invite", result })}
        />
      )}
      {action?.kind === "edit" && (
        <EditUserDialog
          user={action.user}
          teams={teamsByUser.get(action.user.id) ?? []}
          isMe={action.user.id === me.user.id}
          onClose={close}
        />
      )}
      {action?.kind === "vacation" && (
        <VacationDialog person={action.user} initial={action.user.vacation} onClose={close} />
      )}
      {action?.kind === "link" && <LinkDialog mode={action.mode} result={action.result} onClose={close} />}
      {action?.kind === "block" && (
        <ConfirmDialog
          danger
          title={t("people.confirm.blockTitle", { name: action.user.full_name })}
          description={t("people.confirm.blockText")}
          confirmLabel={t("people.actions.block")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => updateUser.mutateAsync({ userId: action.user.id, status: "blocked" })}
          onClose={close}
        />
      )}
      {action?.kind === "unblock" && (
        <ConfirmDialog
          title={t("people.confirm.unblockTitle", { name: action.user.full_name })}
          description={t("people.confirm.unblockText")}
          confirmLabel={t("people.actions.unblock")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => updateUser.mutateAsync({ userId: action.user.id, status: "active" })}
          onClose={close}
        />
      )}
      {action?.kind === "cancel" && (
        <ConfirmDialog
          danger
          title={t("people.confirm.cancelTitle", { name: action.user.full_name })}
          description={t("people.confirm.cancelText")}
          confirmLabel={t("people.actions.cancelInvite")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => cancelInvite.mutateAsync(action.user.id)}
          onClose={close}
        />
      )}
      {action?.kind === "resetMfa" && (
        <ConfirmDialog
          danger
          title={t("people.confirm.resetMfaTitle", { name: action.user.full_name })}
          description={t("people.confirm.resetMfaText")}
          confirmLabel={t("people.actions.resetMfa")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => resetMfa.mutateAsync(action.user.id)}
          onClose={close}
        />
      )}
    </>
  );
}

type PersonRowProps = {
  user: User;
  teams: Team[];
  isMe: boolean;
  admin: boolean;
  canMarkVacation: boolean;
  onAction: (a: Action) => void;
};

function PersonRow({ user: u, teams, isMe, admin, canMarkVacation, onAction }: PersonRowProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const resend = useResendInvitation();
  const resetLink = useCreateResetLink();
  const [error, setError] = useState<unknown>(null);
  const expired = u.invitation_expires_at && new Date(u.invitation_expires_at) < new Date();

  const issue = async (mode: "invite" | "reset") => {
    setError(null);
    try {
      const result = mode === "invite" ? await resend.mutateAsync(u.id) : await resetLink.mutateAsync(u.id);
      onAction({ kind: "link", mode, result });
    } catch (err) {
      setError(err);
    }
  };

  return (
    <TR className="hover:bg-surface-muted/50">
      <TD>
        <div className="flex items-center gap-3">
          <UserAvatar name={u.full_name} className="size-9" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 font-medium">
              <span className="truncate">{u.full_name}</span>
              {isMe && <span className="text-xs font-normal text-muted">({t("common.you")})</span>}
              <VacationBadge vacation={u.vacation} upcoming />
            </div>
            <div className="truncate text-xs text-muted">{u.email}</div>
          </div>
        </div>
        {error !== null && (
          <Alert variant="danger" className="mt-2">
            {errorText(t, error)}
          </Alert>
        )}
      </TD>
      <TD>{t(`roles.${u.role}`)}</TD>
      <TD>
        {teams.length === 0 ? (
          <span className="text-muted">{t("people.noTeams")}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {teams.map((team) => (
              <Badge key={team.id}>
                {team.name}
                {team.members.some((m) => m.user_id === u.id && m.is_manager) && (
                  <span className="text-primary">· {t("teams.manager").toLowerCase()}</span>
                )}
              </Badge>
            ))}
          </div>
        )}
      </TD>
      <TD>
        <div className="flex flex-col items-start gap-0.5">
          <Badge variant={statusBadge[u.status]}>{t(`userStatus.${u.status}`)}</Badge>
          {u.status === "invited" && u.invitation_expires_at && (
            <span className={expired ? "text-xs text-danger" : "text-xs text-muted"}>
              {expired
                ? t("people.invitationExpired")
                : t("people.invitationUntil", { date: formatShortDateTime(u.invitation_expires_at, lang) })}
            </span>
          )}
        </div>
      </TD>
      <TD>
        {u.mfa_enabled ? (
          <span className="inline-flex items-center gap-1 text-success">
            <ShieldCheck className="size-4" aria-hidden="true" /> {t("people.mfaOn")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-muted">
            <ShieldOff className="size-4" aria-hidden="true" /> {t("people.mfaOff")}
          </span>
        )}
      </TD>
      <TD className="whitespace-nowrap text-muted tabular-nums">
        {u.last_seen_at ? formatShortDateTime(u.last_seen_at, lang) : t("common.never")}
      </TD>
      <TD>
        {(admin || u.status !== "invited") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t("people.actionsFor", { name: u.full_name })}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-60">
              {u.status !== "invited" && (
                <DropdownMenuItem asChild>
                  <Link to={`/activity/${u.id}`}>
                    <Activity /> {t("people.actions.timeline")}
                  </Link>
                </DropdownMenuItem>
              )}
              {u.status !== "invited" && (
                <DropdownMenuItem asChild>
                  <Link to={`/timesheets?user=${u.id}`}>
                    <Table2 /> {t("people.actions.timesheet")}
                  </Link>
                </DropdownMenuItem>
              )}
              {u.status === "active" && canMarkVacation && (
                <DropdownMenuItem onSelect={() => onAction({ kind: "vacation", user: u })}>
                  <Palmtree /> {u.vacation ? t("people.actions.vacationEdit") : t("people.actions.vacation")}
                </DropdownMenuItem>
              )}
              {admin && (
                <>
                  <DropdownMenuItem onSelect={() => onAction({ kind: "edit", user: u })}>
                    <Pencil /> {t("people.actions.edit")}
                  </DropdownMenuItem>
                  {u.status === "invited" && (
                    <DropdownMenuItem onSelect={() => void issue("invite")}>
                      <Link2 /> {t("people.actions.resend")}
                    </DropdownMenuItem>
                  )}
                  {u.status === "active" && (
                    <DropdownMenuItem onSelect={() => void issue("reset")}>
                      <KeyRound /> {t("people.actions.resetLink")}
                    </DropdownMenuItem>
                  )}
                  {u.mfa_enabled && !isMe && (
                    <DropdownMenuItem onSelect={() => onAction({ kind: "resetMfa", user: u })}>
                      <RotateCcw /> {t("people.actions.resetMfa")}
                    </DropdownMenuItem>
                  )}
                  {!isMe && <DropdownMenuSeparator />}
                  {!isMe && u.status === "invited" && (
                    <DropdownMenuItem
                      className="text-danger"
                      onSelect={() => onAction({ kind: "cancel", user: u })}
                    >
                      <UserX /> {t("people.actions.cancelInvite")}
                    </DropdownMenuItem>
                  )}
                  {!isMe && u.status !== "blocked" && (
                    <DropdownMenuItem
                      className="text-danger"
                      onSelect={() => onAction({ kind: "block", user: u })}
                    >
                      <Ban /> {t("people.actions.block")}
                    </DropdownMenuItem>
                  )}
                  {!isMe && u.status === "blocked" && (
                    <DropdownMenuItem onSelect={() => onAction({ kind: "unblock", user: u })}>
                      <ShieldCheck /> {t("people.actions.unblock")}
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TD>
    </TR>
  );
}
